import { createContext, FC, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getMonthlyCreditsByType, getTotalCreditsByDashboardType } from "../../../api/broker";
import {
  BarFilters,
  CreditosMensuales,
  CreditosSuborganizacion,
  initalBarFilters,
  MonthYear,
} from "../../../interfaces/SuperAdmin/Pages/iDataCreditos";
import { DashboardType } from "../../../interfaces/SuperAdmin/Pages/iDataDashboard";

interface AllSuborgsDashboardContextProps {
  totalCredits: CreditosSuborganizacion[];
  selectedSuborg: CreditosSuborganizacion | undefined;
  monthlyCredits: CreditosMensuales[];

  months: number;
  startMonth: MonthYear | undefined;
  totalCreditsFilters: BarFilters;
  monthlyCreditsFilters: BarFilters;

  isLoadingTotalCredits: boolean;
  isLoadingMonthlyCredits: boolean;

  changeMonths: (value: number) => void;
  updateStartingMonth: (month: number, year: number) => void;
  handleToggleCredit: (value: string) => void;
  handleToggleMonthlyCredit: (value: string) => void;
}

const AllSuborgsDashboardContext =
  createContext<AllSuborgsDashboardContextProps>(
    {} as AllSuborgsDashboardContextProps
  );

interface Props {
  children: React.ReactNode;
}

const AllSuborgsDashboardProvider: FC<Props> = ({ children }) => {
  // credits tendrá los créditos, y el tipo dependerá de qué filtro tenga
  const [totalCredits, setTotalCredits] = useState<CreditosSuborganizacion[]>(
    []
  );
  const [selectedSuborg, setSelectedSuborg] = useState<
    CreditosSuborganizacion | undefined
  >(undefined);
  const [monthlyCredits, setMonthlyCredits] = useState<CreditosMensuales[]>([]);

  const [months, setMonths] = useState(12);
  const [startMonth, setStartMonth] = useState<MonthYear | undefined>(
    undefined
  );
  const [totalCreditsFilters, setTotalCreditsFilters] =
    useState<BarFilters>(initalBarFilters);
  const [monthlyCreditsFilters, setMonthlyCreditsFilters] =
    useState<BarFilters>(initalBarFilters);

  const [isLoadingTotalCredits, setIsLoadingTotalCredits] = useState(true);
  const [isLoadingMonthlyCredits, setIsLoadingMonthlyCredits] = useState(true);

  const { idOrganization, idSuborg } = useParams();

  // Fetch de creditos totales de una organización
  const fetchCreditos = async () => {
    setIsLoadingTotalCredits(true);
    try {
      const res = await getTotalCreditsByDashboardType(
        DashboardType.SUBORGS,
        { id_organizacion: idOrganization }
      );
      console.log(res);
      if (res.data.statusCode === 200) {
        setTotalCredits(res.data.body as CreditosSuborganizacion[]);
      } else {
        toast.error("Error al obtener los créditos totales");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al obtener los créditos totales");
    } finally {
      setIsLoadingTotalCredits(false);
    }
  };

  // Fetch de creditos mensuales de una suborganizacion
  const fetchMonthlyCredits = async (
    idSuborg: string,
    startMonth: MonthYear | undefined,
    months: string
  ) => {
    setIsLoadingMonthlyCredits(true);
    try {
      // Con esta linea formateamos startMonth para que quede en el formato 'MM/YYYY'
      let firstMonth = startMonth
        ? `${startMonth.month}/${startMonth.year}`
        : undefined;

      const monthlyRes = await getMonthlyCreditsByType(
        DashboardType.SUBORGS,
        idSuborg,
        firstMonth,
        months
      );
      console.log(monthlyRes);

      if (monthlyRes.data.statusCode === 200) {
        if (!startMonth) {
          const { start_month, start_year } = monthlyRes.data.body;
          setStartMonth({ year: start_year, month: start_month });
        }
        setMonthlyCredits(monthlyRes.data.body.sublimaciones);
      } else {
        toast.error("Error al obtener los créditos mensuales");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al obtener los créditos mensuales");
    } finally {
      setIsLoadingMonthlyCredits(false);
    }
  };

  // Ejecutamos el fetch al montar el componente
  useEffect(() => {
    if (idOrganization) {
      fetchCreditos();
    }
  }, [idOrganization]);

  // Cada vez que cambie idSuborg, vamos a buscarla dentro de las suborganizaciones ya traídas
  useEffect(() => {
    if (idSuborg) {
      const foundSuborg = totalCredits.find(
        (item) => item.id_suborganizacion === idSuborg
      );
      setSelectedSuborg(foundSuborg);

      // Fetch de creditos mensuales
      fetchMonthlyCredits(idSuborg, startMonth, String(months));
    } else {
      // Con este if, en caso de que no exista idSuborg, reiniciamos los valores de months y start_month
      setMonths(12);
      setStartMonth(undefined);
    }
  }, [idSuborg, months, startMonth]);

  // Funcion para cambiar la cantidad de meses
  const changeMonths = (value: number) => {
    setMonths(value);
  };

  // Funcion para actualizar el mes de inicio
  const updateStartingMonth = (month: number, year: number) => {
    setStartMonth({ month, year });
  };

  // Función para cambiar filtros
  const handleToggleCredit = (value: string) => {
    setTotalCreditsFilters((prev) => ({
      ...prev,
      credits: prev.credits.map((credit) =>
        credit.value === value
          ? { ...credit, isActive: !credit.isActive }
          : credit
      ),
    }));
  };

  // Función para cambiar filtros de mes
  const handleToggleMonthlyCredit = (value: string) => {
    setMonthlyCreditsFilters((prev) => ({
      ...prev,
      credits: prev.credits.map((credit) =>
        credit.value === value
          ? { ...credit, isActive: !credit.isActive }
          : credit
      ),
    }));
  };

  return (
    <AllSuborgsDashboardContext.Provider
      value={{
        totalCredits,
        selectedSuborg,
        monthlyCredits,
        months,
        startMonth,
        totalCreditsFilters,
        monthlyCreditsFilters,
        isLoadingTotalCredits,
        isLoadingMonthlyCredits,
        changeMonths,
        updateStartingMonth,
        handleToggleCredit,
        handleToggleMonthlyCredit,
      }}
    >
      {children}
    </AllSuborgsDashboardContext.Provider>
  );
};

const useAllSuborgsDashboard = () => useContext(AllSuborgsDashboardContext);

export { AllSuborgsDashboardProvider, useAllSuborgsDashboard };

